export const isNumInRangeIncl = (num: number, [min, max]: [number, number]) => {
  return num >= min && num <= max;
};

/**
 * Rounds a number to 2 decimal places to handle floating point precision issues.
 * This is commonly needed for financial calculations where JavaScript's floating
 * point arithmetic can cause precision errors (e.g., 0.1 + 0.2 = 0.30000000000000004).
 *
 * @param value - The number to round
 * @returns The number rounded to 2 decimal places
 *
 * @example
 * roundToTwoDecimals(0.1 + 0.2) // returns 0.3
 * roundToTwoDecimals(1.234567) // returns 1.23
 */
export const roundToTwoDecimals = (value: number): number => {
  return Math.round(value * 100) / 100;
};

/**
 * Formats a number to exactly 2 decimal places with locale-aware grouping and
 * decimal separators, e.g. 1234567.891 -> "1,234,567.89" in en-US.
 *
 * This is the canonical cost-value display format: it matches the cost column
 * formatting in the consequence register (`pages/consequences/config.tsx`) so
 * widget-aggregated cost values render identically to register cells
 * (RSP-5735).
 *
 * The exact separators are **locale-dependent** — it uses
 * `toLocaleString(undefined, …)`, which honours the runtime locale. The
 * examples below show en-US output; e.g. a `de-DE` runtime yields
 * "1.234.567,89".
 *
 * @param value - The number to format
 * @returns The formatted string (separators per the runtime locale)
 *
 * @example
 * // en-US runtime:
 * formatTwoDecimalPlaces(1000) // returns "1,000.00"
 * formatTwoDecimalPlaces(0.1 + 0.2) // returns "0.30"
 */
export const formatTwoDecimalPlaces = (value: number): string =>
  roundToTwoDecimals(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
